/**
 * SchemaFormEditor — schema-driven editor for ChromeSpec / PaperSpec /
 * WritingStyleSpec.
 *
 * DRY: every field is rendered via the existing ``<FieldRenderer>`` from
 * ``@ando/ui/connectors``. The renderer already understands string /
 * number / boolean / select / password / url + emits canonical inline
 * helper text — we don't roll a separate input library for templates.
 *
 * Spec round-trip: BE accepts an opaque JSON blob and re-validates it
 * server-side via ``_validate_spec(kind, spec)``. The flat dot-path knobs
 * defined in ``TemplateFieldSpecs`` cover ~80% of the day-to-day editing
 * surface; nested arrays (text_elements, decorations) remain best-edited
 * via the v1 raw-JSON inspector, which we surface read-only below the
 * form so power users can copy/paste into the v1 UI when needed.
 */

import type * as React from "react";

import { Trans } from "@lingui/react/macro";

import { FieldRenderer, type FieldSpec } from "@ando/ui";
import type { TemplateKind } from "@ando/core";

import {
  TEMPLATE_FIELD_SPECS,
  readPath,
  writePath,
} from "./TemplateFieldSpecs";

export interface SchemaFormEditorProps {
  /** Drives which field set we render. */
  readonly kind: TemplateKind;
  /** Current spec JSON — mutated via ``onChange``. */
  readonly spec: Record<string, unknown>;
  /** Patched spec emitted on every field change (whole-object replace). */
  readonly onChange: (next: Record<string, unknown>) => void;
  /** Locks every input — used when displaying a built-in spec. */
  readonly readOnly?: boolean;
}

export const SchemaFormEditor: React.FC<SchemaFormEditorProps> = ({
  kind,
  spec,
  onChange,
  readOnly,
}) => {
  const fields = TEMPLATE_FIELD_SPECS[kind];
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((field: FieldSpec) => (
          <FieldRenderer
            key={field.name}
            spec={field}
            value={readPath(spec, field.name)}
            disabled={readOnly}
            onChange={(next) => {
              onChange(writePath(spec, field.name, next));
            }}
          />
        ))}
      </div>

      {/* Read-only spec inspector — lets power users copy the full JSON
          (incl. nested text_elements, decorations) into the v1 editor.
          Always visible so the high-fidelity escape hatch is one click
          away; collapsing it would hide the affordance. */}
      <details className="rounded-md border border-hairline bg-card/40 px-3 py-2 text-xs">
        <summary className="cursor-pointer font-medium text-ink-2">
          <Trans>Spec JSON · ispettore avanzato</Trans>
        </summary>
        <pre className="mt-2 max-h-72 overflow-auto rounded bg-black/[0.04] p-2 font-mono text-[11px] text-ink-2">
          {JSON.stringify(spec, null, 2)}
        </pre>
      </details>
    </div>
  );
};
SchemaFormEditor.displayName = "SchemaFormEditor";
